"use client";

import "react-big-calendar/lib/css/react-big-calendar.css";

import { useEffect, useMemo, useState } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment, { Moment } from "moment-timezone";
import ClickableLink from "../components/utilities/ClickableLink";
import FontAwesome from "../components/utilities/FontAwesome";
import classNames from "classnames";
import { observer } from "mobx-react";
import { useMachine } from "@xstate/react/lib";
import { FetchMachine } from "../components/machines/FetchMachine";
import styles from "./styles/CalendarPage.module.scss";
import { getLocalTimezone } from "../utils/CleversiteUtilities";
import { getFontColorRGB } from "../utils/colorcontrast";
import WatsonApi from "../backends/WatsonApi";
import { useStores } from "../stores/useStores";
import { useTranslateClassName } from "../hooks/useTranslateClassName";
import { renderGoogleLanguage } from "../utils/SchoolBlocksUtilities";
import { Box, Button, Popover } from "@radix-ui/themes";
import useTheme from "../components/radix/useTheme";
import { ReactiveContentItem } from "../components/schoolfeed/SchoolFeedContentItem";
import { TranslatedText } from "../components/utilities/TranslatedText";

const localStorageKey = "hiddenCalendarIds";

function getHiddenCalendarIds(calendars) {
  const hiddenCalendarIds: string[] = [];
  let storageItems: string[] = [];
  for (let i = 0; i < calendars?.length; i++) {
    if (
      !calendars[i].defaultCalendar &&
      !hiddenCalendarIds.includes(calendars[i].id)
    ) {
      hiddenCalendarIds.push(calendars[i].id);
    }
  }
  const storageItem = localStorage.getItem(localStorageKey);
  if (storageItem) {
    storageItems = JSON.parse(storageItem);
  }
  return [...hiddenCalendarIds, ...storageItems];
}

export function CalendarShare(props: { icalUrl: string }) {
  const { icalUrl } = props;
  const radixTheme = useTheme();

  return (
    <Popover.Root>
      <Popover.Trigger>
        <Button variant="outline">
          <FontAwesome prefix={"far"} name="fa-share-square" />
          &nbsp;Add Calendar &nbsp;
          <FontAwesome prefix={"fas"} name="fa-caret-down" />
        </Button>
      </Popover.Trigger>
      <Popover.Content
        container={radixTheme.portalContainer?.current}
        className={styles.dropdownMenu}
        role={"menu"}
      >
        <ClickableLink
          className={styles.dropdownItem}
          href={`https://www.google.com/calendar/render?cid=${icalUrl}`}
        >
          <FontAwesome prefix={"fab"} fixedWidth className="fa-google-plus-g" />{" "}
          Add to Google
        </ClickableLink>
        <ClickableLink
          className={styles.dropdownItem}
          href={icalUrl.replace("https://", "webcal://")}
        >
          <FontAwesome prefix={"fab"} fixedWidth className="fa-windows" /> Add
          to Outlook
        </ClickableLink>
        <ClickableLink
          className={styles.dropdownItem}
          href={icalUrl.replace("https://", "webcal://")}
        >
          <FontAwesome prefix={"fab"} fixedWidth className="fa-apple" /> Add to
          Apple
        </ClickableLink>
        <ClickableLink className={styles.dropdownItem} href={icalUrl}>
          <FontAwesome prefix={"fas"} fixedWidth className="fa-calendar" /> Add
          to other calendar
        </ClickableLink>
      </Popover.Content>
    </Popover.Root>
  );
}

function EventView(props) {
  const time = moment(props.event.start).format("LT");

  return (
    <div
      key={`${props.event.title}${props.event.start}`}
      aria-label={`From calendar ${props.event.resources.calendar?.name || ""} ${moment(props.event.start).format("LT MMMM Do")} ${props.event.title}`}
    >
      <span
        style={{
          backgroundColor: props.event.resources.calendar.color,
          width: "10px",
          height: "10px",
          borderRadius: "100%",
          marginRight: "5px",
          display: "inline-block",
        }}
      />
      {props.event.allDay || <span style={{ fontWeight: 600 }}>{time} </span>}
      {props.event.title}
    </div>
  );
}

interface EventPropResources extends EventContentItem {
  calendar: ICalendar;
}

interface EventProp {
  title: string;
  start: Date;
  end: Date;
  allDay: boolean;
  resources: EventPropResources;
}

interface ICalendar {
  checked: boolean;
  color: string;
  dontInheritCalendar: number;
  defaultCalendar: boolean;
  events: IEvent[];
  icalUrl: string;
  id: string;
  name: string;
}

const CalendarPage = observer(() => {
  const { modalStore, organizationStore, interfaceStore, i18nStore } =
    useStores();
  const [eventSources, setEventSources] = useState<ICalendar[]>([]);
  const [events, setEvents] = useState<EventProp[]>([]);
  const [tz, setTz] = useState<Moment | false>(false);
  const [view, setView] = useState<string>("month");
  const [current, send] = useMachine(FetchMachine);
  const [isCalendarSelectorVisible, setCalendarSelectorVisible] = useState(
    interfaceStore.breakpoint === "break-point-lg" ||
      interfaceStore.breakpoint === "break-point-md",
  );
  const translateClassName = useTranslateClassName();

  const toggleCalendar = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setCalendarSelectorVisible(!isCalendarSelectorVisible);
  };

  // Memoize the localizer so it updates when the locale changes
  // This ensures react-big-calendar uses the correct locale for month/day names
  const localizer = useMemo(() => {
    return momentLocalizer(moment);
  }, [i18nStore.googleCode]);

  const [currentRange, setCurrentRange] = useState({
    start: new Date(
      moment().startOf("month").subtract(3, "days").format("YYYY-MM-DD"),
    ),
    end: new Date(moment().endOf("month").add(3, "days").format("YYYY-MM-DD")),
  });

  useEffect(() => {
    // hydration error if this gets set server-side
    setTz(getLocalTimezone());
  }, []);

  useEffect(() => {
    if (organizationStore?.currentOrganization?.calendars?.length === 1) {
      setEventSources(organizationStore.currentOrganization.calendars);
    } else if (organizationStore?.currentOrganization?.calendars) {
      const hiddenCalendarIds = getHiddenCalendarIds(
        organizationStore.currentOrganization.calendars,
      );
      let sourceList = organizationStore.currentOrganization?.calendars?.map(
        (source) => {
          return {
            ...source,
            checked: !hiddenCalendarIds.includes(source.id),
          };
        },
      );
      //50 chosen after a little testing of what worked well on my connection, I can up or down the hard limit
      if (sourceList.length >= 50) {
        sourceList = sourceList.slice(0, 50);
      }
      setEventSources(sourceList);
    }
  }, []);

  useEffect(() => {
    // Need to save view in state to prevent hydration errors associated with defaultView prop
    if (
      interfaceStore?.breakpoint !== "break-point-lg" ||
      (interfaceStore?.zoomValue ?? 0) >= 250
    ) {
      setView("agenda");
    }
  }, [interfaceStore?.breakpoint, interfaceStore?.zoomValue]);

  useEffect(() => {
    const abortControllers: AbortController[] = [];
    (async () => {
      const checkedCalendars = eventSources.filter((source) => source.checked);
      // If no calendars are selected, clear events
      if (checkedCalendars.length === 0) {
        setEvents([]);
        return;
      }

      send("FETCH");
      try {
        const client = await WatsonApi();

        const results = await Promise.all(
          checkedCalendars.map((calendar) => {
            return new Promise(async (resolve) => {
              try {
                const abortController = new AbortController();
                abortControllers.push(abortController);
                const response =
                  await client.apis.organizations.organizations_get_events_between_dates(
                    {
                      id: organizationStore.currentOrganization.id,
                      start_date: currentRange.start.toISOString(),
                      end_date: currentRange.end.toISOString(),
                      calendarIds: [calendar.id],
                    },
                    {
                      signal: abortController.signal,
                    },
                  );
                const calendarEvents = JSON.parse(response.data);
                resolve(calendarEvents);
              } catch {
                resolve([]);
              }
            });
          }),
        );

        const newEvents: EventProp[] = results.flat().map((evt: any) => {
          const item = new ReactiveContentItem(evt);
          const mapped = {
            title: item.title,
            start: new Date(item.json_data.event.start),
            end: new Date(item.json_data.event.end),
            allDay: item.json_data.event.all_day,
            resources: {
              ...(item as EventContentItem),
              calendar: organizationStore.currentOrganization.calendars.find(
                (c) => c.id === item.source_id,
              ),
            },
          };
          return mapped;
        });
        setEvents(newEvents);
        send("FULFILL");
        renderGoogleLanguage(i18nStore.googleCode);
      } catch {
        send("REJECT");
      }
    })();

    return () => {
      abortControllers.forEach((abortController) => abortController.abort());
    };
  }, [currentRange, eventSources]);

  function handleRangeChange(range) {
    let nextStart, nextEnd;
    if (Array.isArray(range)) {
      nextStart = new Date(
        moment(range[0]).subtract(3, "days").format("YYYY-MM-DD"),
      );
      nextEnd = new Date(
        moment(range[range.length - 1])
          .add(3, "days")
          .format("YYYY-MM-DD"),
      );
    } else {
      nextStart = new Date(
        moment(range.start).subtract(3, "days").format("YYYY-MM-DD"),
      );
      nextEnd = new Date(moment(range.end).add(3, "days").format("YYYY-MM-DD"));
    }

    if (nextStart < currentRange.start || nextEnd > currentRange.end) {
      setCurrentRange({
        start: nextStart,
        end: nextEnd,
      });
    }
  }

  const numCalendars = organizationStore.currentOrganization.calendars.length;

  let content, link;
  if (numCalendars === 0) {
    content = (
      <li key="calendarLabel">
        <span style={{ fontSize: "1.2em", fontWeight: 1000 }}>
          No Calendars Found
        </span>
      </li>
    );
  } else if (numCalendars === 1) {
    const thisCal = organizationStore.currentOrganization.calendars[0];
    const icalUrl = thisCal.icalUrl;

    content = (
      <li key="calendarLabel">
        <span
          style={{ fontSize: "1.2em" }}
          data-value={thisCal.id}
          data-color={thisCal.color}
          data-name={thisCal.name}
        >
          <span
            className="sb-calendar-button-dot"
            role="img"
            aria-hidden="true"
            style={{ backgroundColor: thisCal.color }}
          >
            &nbsp;
          </span>{" "}
          <span className="calendarName">
            {thisCal.name}{" "}
            {thisCal.name.toLowerCase().includes("calendar") ? "" : "Calendar"}
          </span>
        </span>
      </li>
    );

    link = <CalendarShare icalUrl={icalUrl} />;
  } else if (numCalendars > 1) {
    content = (
      <>
        <li key="calendarLabel">
          <span style={{ fontSize: "1.2em", fontWeight: 1000 }}>
            Calendars{" "}
            <span
              className={styles.tooltip}
              data-content={
                "Select the calendars you would like to view.\nEvent details can be seen by clicking on an event."
              }
            >
              <FontAwesome name={"fa-solid fa-circle-info"} />
            </span>
          </span>
        </li>
        <div className="calendarSelector">
          {eventSources.map((calendar) => {
            const checkMarkColor = getFontColorRGB(calendar.color);
            const buttonClassName = classNames({
              ["sb-filter-button"]: true,
              ["sb-calendar-button"]: true,
              ["active"]: calendar.checked,
            });
            return (
              <li key={calendar.id}>
                <button
                  type="button"
                  className={buttonClassName}
                  tabIndex={isCalendarSelectorVisible ? 0 : -1}
                  onClick={() => {
                    const hiddenCalendarIds = getHiddenCalendarIds(
                      organizationStore.currentOrganization.calendars,
                    );
                    if (hiddenCalendarIds.includes(calendar.id)) {
                      localStorage.setItem(
                        localStorageKey,
                        JSON.stringify(
                          hiddenCalendarIds.filter((c) => c !== calendar.id),
                        ),
                      );
                    } else if (calendar.checked) {
                      localStorage.setItem(
                        localStorageKey,
                        JSON.stringify([
                          ...hiddenCalendarIds.filter((c) => c !== calendar.id),
                          calendar.id,
                        ]),
                      );
                    }
                    setEventSources((sources: ICalendar[]) => {
                      return sources.map((source) => {
                        if (source.id === calendar.id) {
                          return { ...source, checked: !source.checked };
                        }
                        return source;
                      });
                    });
                  }}
                  data-value={calendar.id}
                  data-color={calendar.color}
                  data-name={calendar.name}
                >
                  <span
                    className="sb-calendar-button-dot"
                    role="img"
                    aria-hidden="true"
                    style={{
                      backgroundColor: calendar.checked
                        ? calendar.color
                        : "#eee",
                      borderColor: calendar.color,
                    }}
                  >
                    {calendar.checked && (
                      <FontAwesome
                        style={{ color: checkMarkColor }}
                        name={"fa-solid fa-check fa-sm"}
                        prefix={"fas"}
                      />
                    )}
                    &nbsp;
                  </span>{" "}
                  <span className="calendarName">{calendar.name}</span>
                </button>
              </li>
            );
          })}
        </div>
      </>
    );

    link = (
      <Button
        tabIndex={isCalendarSelectorVisible ? 1 : 0}
        variant="outline"
        onClick={() =>
          modalStore.addModal({
            type: "calendarList",
            calendars: eventSources,
          })
        }
      >
        <FontAwesome
          prefix={"far"}
          name="fa-share-square"
          style={{ marginRight: "0.4em" }}
        />
        <TranslatedText>Export Calendar</TranslatedText>
      </Button>
    );
  }

  function ListEventView(props) {
    return (
      <button
        key={`${props.event.title}${props.event.start}`}
        aria-label={`${props.event.title} at ${moment(props.event.start).format("LT MMMM Do")}
                       until ${moment(props.event.end).format("LT MMMM Do")},
                       from calendar ${props.event.resources.calendar?.name || ""}`}
        tabIndex={0}
      >
        <span
          style={{
            backgroundColor: props.event.resources.calendar.color,
            width: "10px",
            height: "10px",
            borderRadius: "100%",
            marginRight: "5px",
            display: "inline-block",
          }}
        />
        {props.event.title}
      </button>
    );
  }
  const calendarSelector = classNames({
    [styles.sbCalendarSelectorContainer]: true,
    [styles.sbCalendarSelectorContainerHidden]: !isCalendarSelectorVisible,
  });
  const toggleCalendarListButtonClassNames = classNames({
    [styles.toggleCalendarListButton]: true,
    [styles.toggleCalendarListButtonCollapsed]: !isCalendarSelectorVisible,
  });
  const collapseClassNames = classNames({
    [styles.collapsableContainer]: numCalendars > 1,
    [styles.collapsableContainerHidden]: !isCalendarSelectorVisible,
  });
  const calendarDisplayContainerClassNames = classNames({
    [styles.calendarDisplayContainer]: true,
    [styles.calendarDisplayContainerCollapsed]: !isCalendarSelectorVisible,
  });
  const containerClassName = classNames({
    ["sb-main-content"]: true,
    [translateClassName]: true,
  });

  return (
    <div id="sb-main-content" className={containerClassName}>
      <div className="sb-calendar-block">
        <div className={calendarSelector}>
          <div className="calendarList sb-calendar-margin">
            <button
              aria-label={
                (isCalendarSelectorVisible ? "Collapse" : "Expand") + " Menu"
              }
              type={"button"}
              className={toggleCalendarListButtonClassNames}
              onClick={toggleCalendar}
            >
              <FontAwesome
                name={"fas fa-arrow-left"}
                rotate={isCalendarSelectorVisible ? undefined : 180}
                prefix={"fas"}
              />
            </button>
            <ul>
              <div className={collapseClassNames}>
                {content}
                <>
                  <Box mt={"1"}>{link}</Box>
                  <div>
                    {tz && (
                      <span id="sb-calendar-timezone-display">
                        Timezone: {tz.format("z (Z)")}
                      </span>
                    )}
                  </div>
                </>
              </div>
            </ul>
          </div>
        </div>
        <div className={calendarDisplayContainerClassNames}>
          <div id="calendarLoading">
            <span key="loadingText">
              {current.value === "PENDING" && (
                <TranslatedText>Loading calendar events...</TranslatedText>
              )}
            </span>
            <div className={styles.calendar}>
              <Calendar
                localizer={localizer}
                events={events.filter((event) => {
                  const source = eventSources.find(
                    (source) => source.id === event.resources.source_id,
                  );
                  return source?.checked || numCalendars === 1;
                })}
                onRangeChange={handleRangeChange}
                components={{
                  month: {
                    event: EventView,
                  },
                  agenda: {
                    event: ListEventView,
                  },
                }}
                showAllEvents={true}
                eventPropGetter={() => ({
                  style: {
                    backgroundColor: "#eee",
                    color: "black",
                    fontSize: "12px",
                  },
                })}
                onSelectEvent={(event) => {
                  modalStore.addModal({
                    type: "schoolBlocksEvent",
                    event: event.resources,
                    posthog_params: {
                      modal_origin: "calendar_page",
                    },
                  });
                }}
                view={view}
                onView={(view) => setView(view)}
                onKeyPressEvent={(event, e) => {
                  if (e.key === "Enter") {
                    modalStore.addModal({
                      type: "schoolBlocksEvent",
                      event: event.resources,
                      posthog_params: {
                        modal_origin: "calendar_page",
                      },
                    });
                  }
                }}
                messages={{
                  agenda: <TranslatedText>List</TranslatedText>,
                  previous: <TranslatedText>Previous</TranslatedText>,
                  next: <TranslatedText>Next</TranslatedText>,
                  today: <TranslatedText>Today</TranslatedText>,
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default CalendarPage;
